import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { t, getLanguage } from '@/i18n';
import { arrivalHeatmap, GATES, GATE_CAPACITY, HOURS } from '@/data/siteStats';
import { HelpTip } from '@/components/ui/help-tip';

const locale = () => (getLanguage() === 'en' ? 'en-GB' : 'cs-CZ');
// 1 Jan 2024 was a Monday, so weekday 1–5 maps to Mon–Fri.
const weekdayLabel = (wd) => new Date(2024, 0, wd).toLocaleDateString(locale(), { weekday: 'short' });

// Cell colour from the share of the gate's hourly capacity; red above capacity.
function cellStyle(avg, capacity) {
  if (avg > capacity) return { backgroundColor: 'hsl(var(--destructive) / 0.85)', color: 'hsl(var(--destructive-foreground))' };
  const alpha = Math.min(1, avg / capacity) * 0.8;
  return { backgroundColor: `hsl(var(--chart-1) / ${alpha.toFixed(2)})`, color: alpha > 0.68 ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))' };
}

// Weekday × hour heatmap of average arrivals against the gate capacity.
export const ArrivalHeatmap = ({ days }) => {
  const [gate, setGate] = useState('CLS');
  const rows = useMemo(() => arrivalHeatmap(days, gate), [days, gate]);
  const capacity = GATE_CAPACITY[gate];
  const fmt = (n) => n.toLocaleString(locale(), { maximumFractionDigits: 1 });

  return (
    <Card className="p-4 space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <div className="text-sm font-semibold flex items-center gap-1">{t('stats.site.heatmapTitle')} <HelpTip id="heatmap" /></div>
          <div className="text-xs text-muted-foreground">
            {t('stats.site.heatmapHint').replace('{capacity}', capacity)}
          </div>
        </div>
        <ToggleGroup type="single" value={gate} onValueChange={v => v && setGate(v)} className="ml-auto">
          {GATES.map(g => (
            <ToggleGroupItem key={g} value={g} className="text-xs h-8 px-3">{t(`stats.site.gate.${g}`)}</ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <div className="overflow-x-auto">
        <table className="text-[10px] tabular-nums border-separate border-spacing-0.5">
          <thead>
            <tr>
              <th />
              {HOURS.map(h => <th key={h} className="font-normal text-muted-foreground w-7">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.weekday}>
                <th className="font-medium text-muted-foreground pr-1 text-left">{weekdayLabel(row.weekday)}</th>
                {row.cells.map(c => (
                  <td
                    key={c.hour}
                    className="h-7 w-7 text-center rounded-sm"
                    style={cellStyle(c.avg, capacity)}
                    title={`${weekdayLabel(row.weekday)} ${c.hour}:00 · ${fmt(c.avg)}`}
                  >
                    {c.avg >= 0.5 ? fmt(c.avg) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-muted-foreground">{t('stats.site.heatmapLegend')}</div>
    </Card>
  );
};
